import { IconCircleCheck } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import type { OrderStatus } from "../../../../components/common/chip";
import { formatDateTime } from "../../utils/order-status";

type StepState = "done" | "current" | "pending";

interface TimelineStep {
  label: string;
  date: string | null;
  state: StepState;
}

interface OrderTimelineProps {
  status: OrderStatus;
  dateOrder?: string | null;
  shippedAt?: string | null;
  deliveredAt?: string | null;
}

const STEPS = [
  "Order Placed",
  "Payment Confirmed",
  "Processing",
  "Shipped",
  "Delivered",
] as const;

const CURRENT_INDEX: Record<OrderStatus, number> = {
  processing: 2,
  inTransit: 3,
  delivered: 4,
  validated: 4,
  cancelled: 2,
  rejected: 2,
};

function buildSteps({
  status,
  dateOrder,
  shippedAt,
  deliveredAt,
}: OrderTimelineProps): TimelineStep[] {
  const current = CURRENT_INDEX[status];
  const placedAt = dateOrder ? formatDateTime(dateOrder) : null;
  const shippedDisplay = shippedAt ? formatDateTime(shippedAt) : null;
  const deliveredDisplay = deliveredAt ? formatDateTime(deliveredAt) : null;

  const stepDates: (string | null)[] = [
    placedAt,
    placedAt,
    placedAt,
    shippedDisplay,
    deliveredDisplay,
  ];

  return STEPS.map((label, i) => ({
    label,
    state: i < current ? "done" : i === current ? "current" : "pending",
    date: i > current ? null : stepDates[i],
  }));
}

function StepIcon({ state }: { state: StepState }) {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center">
      {state === "current" ? (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accentGreen">
          <IconCircleCheck size={22} className="text-bgWhite" />
        </div>
      ) : state === "done" ? (
        <IconCircleCheck size={22} className="text-accentGreen" />
      ) : (
        <div className="h-[22px] w-[22px] rounded-full border-2 border-textMuted bg-bgWhite" />
      )}
    </div>
  );
}

export function OrderTimeline(props: OrderTimelineProps) {
  const steps = buildSteps(props);

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
      <Heading variant="h3" tone="dark">Order Timeline</Heading>
      <div className="flex flex-col">
        {steps.map((step, i) => {
          const lineGreen = step.state === "done";
          const isLast = i === steps.length - 1;
          return (
            <div key={step.label} className="flex gap-4">
              <div className="flex flex-col items-center">
                <StepIcon state={step.state} />
                {!isLast && (
                  <div className={`my-1 w-0.5 flex-1 min-h-[56px] ${lineGreen ? "bg-accentGreen" : "bg-textMuted"}`} />
                )}
              </div>
              <div className="flex flex-col gap-0.5 pb-6 last:pb-0">
                <Text
                  variant={step.state !== "pending" ? "bodyBold" : "body"}
                  tone={step.state !== "pending" ? "dark" : "muted"}
                >
                  {step.label}
                </Text>
                <Text variant="bodySm" tone="muted">{step.date ?? "–"}</Text>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
